import { motion } from 'framer-motion';
import { SEO } from '../components/seo/SEO';
import content from '../content.json';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

export function Privacy() {
  const { privacy } = content.legal;

  return (
    <>
      <SEO 
        title={`${privacy.title} | DakshTechInfo LTD`}
        description="Privacy Policy for DakshTechInfo LTD - Learn how we collect, use, and protect your personal information."
        canonical="https://dakshtechinfo.uk/privacy"
      />
      
      <div data-testid="privacy-page">
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="max-w-3xl mx-auto"
            >
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl font-bold mb-4"
              >
                {privacy.title}
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-muted-foreground mb-8"
              >
                Last updated: {privacy.lastUpdated}
              </motion.p>
              
              <motion.div variants={itemVariants} className="prose prose-slate dark:prose-invert max-w-none">
                <p>{privacy.content}</p>
                
                <h2>1. Information We Collect</h2>
                <p>
                  When you use our website, we may collect the following types of information:
                </p>
                <ul>
                  <li><strong>Contact Information:</strong> Name, email address, company name, phone number when you submit our contact form.</li>
                  <li><strong>Technical Information:</strong> IP address (hashed for privacy), browser type, device information, and usage patterns.</li>
                  <li><strong>Cookies:</strong> We use essential cookies to remember your preferences (such as dark mode settings).</li>
                </ul>

                <h2>2. How We Use Your Information</h2>
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Respond to your enquiries and provide the services you request</li>
                  <li>Improve our website and services</li>
                  <li>Comply with legal obligations</li>
                  <li>Protect against fraudulent or malicious activity</li>
                </ul>

                <h2>3. Data Storage and Security</h2>
                <p>
                  Your data is stored securely using Google Cloud Firestore with encryption at rest and in transit. 
                  We implement industry-standard security measures to protect your personal information.
                </p>

                <h2>4. Data Retention</h2>
                <p>
                  We retain your contact form submissions for as long as necessary to respond to your enquiry and 
                  for our legitimate business purposes. You can request deletion of your data at any time.
                </p>

                <h2>5. Your Rights</h2>
                <p>Under UK GDPR, you have the right to:</p>
                <ul>
                  <li>Access your personal data</li>
                  <li>Rectify inaccurate data</li>
                  <li>Request deletion of your data</li>
                  <li>Object to processing of your data</li>
                  <li>Data portability</li>
                </ul>

                <h2>6. Third-Party Services</h2>
                <p>
                  We use Google Cloud services to host our website and store data. These services are 
                  compliant with GDPR and maintain appropriate security certifications.
                </p>

                <h2>7. Contact Us</h2>
                <p>
                  If you have questions about this privacy policy or wish to exercise your data rights, 
                  please contact us at: <a href="mailto:manasa.lingala@dakshtechinfo.uk">manasa.lingala@dakshtechinfo.uk</a>
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export function Terms() {
  const { terms } = content.legal;

  return (
    <>
      <SEO 
        title={`${terms.title} | DakshTechInfo LTD`}
        description="Terms and Conditions for DakshTechInfo LTD - Read our terms of service and usage policies."
        canonical="https://dakshtechinfo.uk/terms"
      />
      
      <div data-testid="terms-page">
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="max-w-3xl mx-auto"
            >
              <motion.h1 
                variants={itemVariants}
                className="text-4xl sm:text-5xl font-bold mb-4"
              >
                {terms.title}
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-muted-foreground mb-8"
              >
                Last updated: {terms.lastUpdated}
              </motion.p>
              
              <motion.div variants={itemVariants} className="prose prose-slate dark:prose-invert max-w-none">
                <p>{terms.content}</p>
                
                <h2>1. Acceptance of Terms</h2>
                <p>
                  By accessing and using the DakshTechInfo LTD website, you accept and agree to be bound by 
                  these Terms and Conditions. If you do not agree to these terms, please do not use our website.
                </p>

                <h2>2. Services Description</h2>
                <p>
                  DakshTechInfo LTD provides cloud consulting, DevOps, and platform engineering services. 
                  The specific terms of any engagement will be detailed in a separate service agreement.
                </p>

                <h2>3. Intellectual Property</h2>
                <p>
                  All content on this website, including text, graphics, logos, and software, is the property 
                  of DakshTechInfo LTD and is protected by intellectual property laws. You may not reproduce, 
                  distribute, or create derivative works without our written permission.
                </p>

                <h2>4. Use of Website</h2>
                <p>You agree to use this website only for lawful purposes and in a way that does not:</p>
                <ul>
                  <li>Infringe the rights of others</li>
                  <li>Restrict or inhibit anyone else's use of the website</li>
                  <li>Attempt to gain unauthorised access to our systems</li>
                  <li>Transmit any harmful code or content</li>
                </ul>

                <h2>5. Limitation of Liability</h2>
                <p>
                  DakshTechInfo LTD provides this website "as is" without any warranties. We shall not be 
                  liable for any indirect, incidental, or consequential damages arising from your use of 
                  this website.
                </p>

                <h2>6. External Links</h2>
                <p>
                  Our website may contain links to third-party websites. We are not responsible for the 
                  content or privacy practices of these external sites.
                </p>

                <h2>7. Governing Law</h2>
                <p>
                  These terms are governed by the laws of England and Wales. Any disputes shall be subject 
                  to the exclusive jurisdiction of the courts of England and Wales.
                </p>

                <h2>8. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these terms at any time. Changes will be effective 
                  immediately upon posting to this website.
                </p>

                <h2>9. Contact</h2>
                <p>
                  For questions about these terms, please contact us at: 
                  <a href="mailto:manasa.lingala@dakshtechinfo.uk">manasa.lingala@dakshtechinfo.uk</a>
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}
